import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { getMappedChannelNames } from "@/shared/helpers/getMappedChannelNames";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { ChannelDataWithSourceAndTotal } from "@/shared/types";
import { axiosApi } from "@/Utils";

type CampaignsROIsTotals = {
  totalSpend: number;
  totalRevenue: number;
  totalRoi: number;
};

type CampaignsROIsResponse = {
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: CampaignsROIsTotals;
};

const getCampaignsROIsV2 = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle,
  campaigns = [],
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
  campaigns?: string[] | undefined;
}): Promise<CampaignsROIsResponse> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    geographies,
    graphView,
    tableView,
    geoViewToggle,
    businessUnitToggle,
    campaigns,
  };

  return axiosApi.get("/api/v2/campaigns", {
    params: payload,
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

const _getCampaignsROIsNest = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle,
  campaigns = [],
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
  campaigns?: string[] | undefined;
}): Promise<CampaignsROIsResponse> => {
  const body: Record<string, any> = {};

  // Include string values only if they are truthy
  if (brand) body.brand = brand;
  if (category) body.category = category;
  if (product) body.product = product;
  if (startDate) body.startDate = startDate;
  if (endDate) body.endDate = endDate;

  // Always include required enum values
  if (tableView) body.tableView = tableView;
  if (graphView) body.graphView = graphView;
  if (geoViewToggle) body.geoViewToggle = geoViewToggle;
  if (businessUnitToggle) body.businessUnitToggle = businessUnitToggle;

  // Include arrays only if they have length > 0
  if (channels && channels.length > 0) body.channels = channels;
  if (geographies && geographies.length > 0) body.geographies = geographies;
  if (campaigns && campaigns.length > 0) body.campaigns = campaigns;

  return axiosApi.post(`/api/campaigns`, body, {
    baseURL: NEST_BACKEND_URL,
  });
};

export const useCampaignsROIsV3 = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle,
  config,
  campaigns = [],
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  startDate: string;
  endDate: string;
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  geographies?: string[];
  config?: QueryConfig<typeof getCampaignsROIsV2 | typeof _getCampaignsROIsNest>;
  campaigns?: string[];
}) => {
  const endpointType = useModuleEndpoint("campaigns");

  const queryKey = useMemo(
    () => [
      "campaigns-roi-v3",
      brand,
      startDate,
      endDate,
      geographies,
      graphView,
      tableView,
      geoViewToggle,
      businessUnitToggle,
      campaigns,
      category,
      product,
      channels,
      endpointType,
    ],
    [
      brand,
      startDate,
      endDate,
      geographies,
      graphView,
      tableView,
      geoViewToggle,
      businessUnitToggle,
      campaigns,
      category,
      product,
      channels,
      endpointType,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getCampaignsROIsNest({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        geographies,
        graphView,
        tableView,
        geoViewToggle,
        businessUnitToggle,
        campaigns,
      });
    } else {
      return getCampaignsROIsV2({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        geographies,
        graphView,
        tableView,
        geoViewToggle,
        businessUnitToggle,
        campaigns,
      });
    }
  }, [
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    geographies,
    graphView,
    tableView,
    geoViewToggle,
    businessUnitToggle,
    campaigns,
    endpointType,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
    select: (data) => ({
      ...data,
      tableView: getMappedChannelNames(data.tableView),
      chartView: getMappedChannelNames(data.chartView),
    }),
  });
};
